import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Grid,
  TableSortLabel,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Checkbox,
  FormHelperText,
  Link,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useBusiness } from '../context/BusinessContext';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { toArray } from '../utils/apiData';
import { ROLE_OPTIONS, roleLabel } from '../utils/roles';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const AdminUsersPage = () => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const { user: currentUser } = useAuth();
  const { businessName } = useBusiness();
  const [users, setUsers] = useState([]);
  const [alertTypes, setAlertTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [sortBy, setSortBy] = useState('username');
  const [sortOrder, setSortOrder] = useState('asc');

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'user',
    active: true,
    sms_consent: false,
    alert_subscriptions: [],
  });

  const loadUsers = useCallback(async () => {
    try {
      const params = {
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      const response = await usersAPI.getAll(params);
      setUsers(toArray(response.data, 'users'));
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    const loadAlertTypes = async () => {
      try {
        const response = await usersAPI.getAlertTypes();
        setAlertTypes(toArray(response.data, 'alert types'));
      } catch (error) {
        console.error('Error loading alert types:', error);
      }
    };
    loadAlertTypes();
  }, []);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleOpenDialog = (user = null) => {
    fields.reset();
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        role: user.role || 'user',
        active: user.active !== false,
        sms_consent: user.sms_consent || false,
        alert_subscriptions: user.alert_subscriptions || [],
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        role: 'user',
        active: true,
        sms_consent: false,
        alert_subscriptions: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    fields.clear(name);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.checked }));
  };

  const handleAlertToggle = (key) => {
    setFormData((prev) => ({
      ...prev,
      alert_subscriptions: prev.alert_subscriptions.includes(key)
        ? prev.alert_subscriptions.filter((k) => k !== key)
        : [...prev.alert_subscriptions, key],
    }));
  };

  const handleSubmit = async () => {
    if (!fields.validate({ password: !editingUser && !formData.password && 'Password is required for new users' })) return;
    try {
      if (editingUser) {
        const updateData = {
          username: formData.username,
          email: formData.email,
          first_name: formData.first_name || null,
          last_name: formData.last_name || null,
          phone: formData.phone || null,
          role: formData.role,
          active: formData.active,
          sms_consent: formData.sms_consent,
          alert_subscriptions: formData.alert_subscriptions,
        };
        await usersAPI.update(editingUser.id, updateData);
        if (formData.password) {
          await usersAPI.setPassword(editingUser.id, formData.password);
        }
      } else {
        await usersAPI.create({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name || null,
          last_name: formData.last_name || null,
          phone: formData.phone || null,
          role: formData.role,
          active: formData.active,
          sms_consent: formData.sms_consent,
          alert_subscriptions: formData.alert_subscriptions,
        });
      }
      handleCloseDialog();
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      notify.error(errorMessage(error, 'Error saving user'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (id) => setDeleteTarget(id);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await usersAPI.delete(deleteTarget);
      setDeleteTarget(null);
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      notify.error(errorMessage(error, 'Error deleting user'));
    }
  };

  const formatName = (user) => {
    const parts = [user.first_name, user.last_name].filter(Boolean);
    return parts.length ? parts.join(' ') : '-';
  };

  const formatAlerts = (user) => {
    const count = (user.alert_subscriptions || []).length;
    if (!count) return 'None';
    // Subscriptions without consent never send — say so rather than imply they do.
    if (!user.sms_consent) return `${count} of ${alertTypes.length} (no SMS consent)`;
    return `${count} of ${alertTypes.length}`;
  };


  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'flex-start', sm: 'space-between' },
          alignItems: 'center',
          gap: { xs: 1.5, sm: 0 },
          mb: 3,
        }}
      >
        <Typography variant="h4">Users</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            minWidth: { xs: 0, sm: 'auto' },
            width: { xs: 32, sm: 'auto' },
            height: { xs: 32, sm: 'auto' },
            px: { xs: 0, sm: 2 },
            '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Add User
          </Box>
        </Button>
      </Box>
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'username'}
                  direction={sortBy === 'username' ? sortOrder : 'asc'}
                  onClick={() => handleSort('username')}
                >
                  Username
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'email'}
                  direction={sortBy === 'email' ? sortOrder : 'asc'}
                  onClick={() => handleSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'first_name'}
                  direction={sortBy === 'first_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('first_name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'role'}
                  direction={sortBy === 'role' ? sortOrder : 'asc'}
                  onClick={() => handleSort('role')}
                >
                  Role
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'active'}
                  direction={sortBy === 'active' ? sortOrder : 'asc'}
                  onClick={() => handleSort('active')}
                >
                  Active
                </TableSortLabel>
              </TableCell>
              <TableCell>Alerts</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={7} />}
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell align="center" sx={actionsKabobCellSx}>
                  <RowActionsMenu
                    actions={[
                      { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(user) },
                      {
                        key: 'delete',
                        label: 'Delete',
                        icon: <DeleteIcon fontSize="small" />,
                        onClick: () => handleDeleteClick(user.id),
                        color: 'error',
                        disabled: currentUser?.id === user.id,
                        title: currentUser?.id === user.id ? 'Cannot delete your own account' : '',
                      },
                    ]}
                  />
                </TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.email || '-'}</TableCell>
                <TableCell>{formatName(user)}</TableCell>
                <TableCell>{roleLabel(user.role)}</TableCell>
                <TableCell>{user.active ? 'Yes' : 'No'}</TableCell>
                <TableCell>{formatAlerts(user)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="username"
                  label="Username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="password"
                  label={editingUser ? 'New password (leave blank to keep current)' : 'Password'}
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  {...fields.props('password')}
                  required={!editingUser}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  fullWidth
                  disableClearable
                  options={ROLE_OPTIONS}
                  getOptionLabel={(r) => r.label}
                  isOptionEqualToValue={(r, v) => r.value === v?.value}
                  filterOptions={createPrefixFilter((r) => r.label)}
                  value={ROLE_OPTIONS.find((r) => r.value === formData.role) ?? ROLE_OPTIONS[0]}
                  onChange={(e, newValue) =>
                    setFormData((prev) => ({ ...prev, role: newValue?.value ?? '' }))
                  }
                  renderInput={(params) => <TextField {...params} label="Role" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="first_name"
                  label="First Name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="last_name"
                  label="Last Name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="active"
                      checked={formData.active}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Text Message Alerts
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Get time-sensitive veterinary and livestock alerts sent directly to your phone.
                  This includes health events, breeding updates, and foaling notifications for
                  the horses at the ranch.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="phone"
                  label="Phone (for SMS alerts)"
                  value={formData.phone}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="+15551234567"
                  helperText="Include country code for SMS low-stock alerts"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="sms_consent"
                      checked={formData.sms_consent}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="User consents to receiving alerts via SMS"
                />
                <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
                  I agree to receive SMS text alerts from {businessName} regarding veterinary and
                  inventory updates for the horses at the ranch. Message frequency varies. Message
                  and data rates may apply. Reply STOP to opt out at any time, or HELP for
                  assistance. Consent is not a condition of any service. View our{' '}
                  <Link href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link href="/terms" target="_blank" rel="noopener noreferrer">
                    Terms &amp; Conditions
                  </Link>
                  .
                </FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" disabled={!formData.sms_consent} fullWidth>
                  <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                    Which alerts to send
                  </FormLabel>
                  <FormHelperText sx={{ mt: 0, mb: 1 }}>
                    {formData.sms_consent
                      ? 'Only the alerts ticked here are texted to this user. Nothing is sent if none are ticked.'
                      : 'Tick "User consents to receiving alerts via SMS" above to choose alerts.'}
                  </FormHelperText>
                  <FormGroup>
                    {alertTypes.map((alertType) => (
                      <Box key={alertType.key} sx={{ mb: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.alert_subscriptions.includes(alertType.key)}
                              onChange={() => handleAlertToggle(alertType.key)}
                            />
                          }
                          label={alertType.label}
                        />
                        <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
                          {alertType.description}
                        </FormHelperText>
                      </Box>
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete User"
        message="Permanently delete this user? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default AdminUsersPage;
